import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  Container, 
  Grid, 
  Divider, 
  useTheme, 
  useMediaQuery 
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import ShieldIcon from '@mui/icons-material/Shield';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link, useNavigate, useLocation } from 'react-router-dom';

interface PublicLayoutProps {
  children: React.ReactNode;
}

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Features', path: '/#features' },
  { label: 'How It Works', path: '/#how-it-works' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false);
    if (path.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate(path);
      } else {
        const id = path.replace('/#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Sticky Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: 'rgba(255, 255, 255, 0.92)', 
          backdropFilter: 'blur(16px)', 
          borderBottom: '1px solid #E2E8F0',
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 72 }}>
            {/* Logo */}
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                gap: 1.5
              }}
            >
              <Box 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  bgcolor: '#1976D2', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(25, 118, 210, 0.3)'
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', lineHeight: 1 }}>
                  S
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="800" sx={{ color: '#0F172A', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
                  Seek<span style={{ color: '#1976D2' }}>TRU</span>
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, letterSpacing: '0.5px', display: 'block', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Smart Lost & Found
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation Links */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  return (
                    <Button
                      key={link.label}
                      onClick={() => handleNavClick(link.path)}
                      sx={{ 
                        color: isActive ? '#1976D2' : '#334155', 
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        px: 1,
                        '&:hover': { color: '#1976D2', bgcolor: 'transparent' }
                      }}
                    >
                      {link.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* CTA Button / Mobile Menu Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                startIcon={<SchoolIcon />}
                sx={{
                  bgcolor: '#1976D2',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  px: { xs: 1.5, sm: 2.5 },
                  py: 1,
                  borderRadius: '24px',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  '&:hover': {
                    bgcolor: '#1565C0',
                    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                School Login
              </Button>

              {isMobile && (
                <IconButton 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle mobile menu"
                  sx={{ color: '#0F172A' }}
                >
                  {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="top"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer,
          '& .MuiDrawer-paper': {
            top: 72,
            px: 3,
            py: 2,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            bgcolor: '#FFFFFF'
          }
        }}
      >
        <List>
          {navLinks.map((link) => (
            <ListItem key={link.label} disablePadding>
              <ListItemButton 
                onClick={() => handleNavClick(link.path)}
                sx={{ py: 1.5, borderRadius: 1 }}
              >
                <ListItemText 
                  primary={link.label} 
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.05rem', color: '#0F172A' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <ListItem disablePadding>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
              startIcon={<SchoolIcon />}
              sx={{
                bgcolor: '#1976D2',
                py: 1.2,
                borderRadius: '24px',
                fontWeight: 600
              }}
            >
              School Portal Login
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Page Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: '#0F172A', color: '#94A3B8', pt: 8, pb: 5, mt: 'auto' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Column 1: Brand & Mission */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box 
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    borderRadius: 2, 
                    bgcolor: '#1976D2', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', lineHeight: 1 }}>
                    S
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                  Seek<span style={{ color: '#60A5FA' }}>TRU</span>
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
                The intelligent, privacy-compliant lost and found platform designed for Australian schools, colleges, and childcare centers. Returning what matters, effortlessly.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#38BDF8', fontWeight: 600, bgcolor: 'rgba(56, 189, 248, 0.1)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  🇦🇺 Engineered for Australian Schools
                </Typography>
              </Box>
            </Grid>

            {/* Column 2: Quick Links */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Explore
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Link to="/" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem' }}>Home</Link>
                <Link to="/#features" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem' }}>Features</Link>
                <Link to="/#how-it-works" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem' }}>How It Works</Link>
                <Link to="/faq" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem' }}>FAQ</Link>
                <Link to="/contact" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem' }}>Contact Us</Link>
              </Box>
            </Grid>

            {/* Column 3: Portals & Legal */}
            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Portals & Privacy
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Link to="/login" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SchoolIcon fontSize="inherit" /> School Login
                </Link>
                <Link to="/privacy-policy" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ShieldIcon fontSize="inherit" /> Privacy Policy & Terms
                </Link>
                <Link to="/faq" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HelpOutlineIcon fontSize="inherit" /> Parent FAQs
                </Link>
              </Box>
            </Grid>

            {/* Column 4: Contact & Support */}
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Get In Touch
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" sx={{ color: '#60A5FA' }} />
                <a href="mailto:info@seektru.com.au" style={{ color: '#FFFFFF', textDecoration: 'none', fontWeight: 500 }}>
                  info@seektru.com.au
                </a>
              </Typography>
              <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.85rem' }}>
                Need help onboarding your school? Our Australian support team is ready to assist.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  mt: 2,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#60A5FA',
                    bgcolor: 'rgba(96, 165, 250, 0.1)'
                  }
                }}
              >
                Request a Demo
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} SeekTRU. All rights reserved.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              Built with privacy and compliance in Australia (Privacy Act 1988)
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
